import React, { useEffect, useState } from "react";
import FEPaginatedTable from "@components/Tables/FEPaginatedTable/FEPaginatedTable";
import { capitalizeFirstLetter } from "@utils/helperFunctions";
import { useNavigate } from "react-router";
import { sortItemsByAttribute, sortItemsByDate } from "../api";

function SharedSurveys({ surveysData, filtering }): JSX.Element {
  const navigate = useNavigate();
  const [sortedSurveys, setSortedSurveys] = useState(surveysData);
  const [sortBy, setSortBy] = useState({
    name: "",
    status: "",
    sentBy: "",
    sentTo: "",
    sentOn: "",
  });

  useEffect(() => {
    if (surveysData.data) {
      setSortedSurveys({ ...sortedSurveys, data: surveysData.data });
    }
  }, [surveysData]);

  interface SharedSurvey {
    id: string;
    checked: boolean;
    attributes: {
      name: JSX.Element;
      status: JSX.Element;
      sentBy: string;
      sentTo: string;
      sentOn: string;
    };
  }

  const columnHeaders = [
    {
      label: "Survey Name",
      width: 3,
      sorted: sortBy.name,
      sortable: true,
    },
    {
      label: "Status",
      width: 2,
      sorted: sortBy.status,
      sortable: true,
    },
    {
      label: "Sent By",
      width: 2,
      sorted: sortBy.sentBy,
      sortable: true,
    },
    {
      label: "Sent To",
      width: 2,
      sorted: sortBy.sentTo,
      sortable: true,
    },
    {
      label: "Sent On",
      width: 2,
      sorted: sortBy.sentOn,
      sortable: true,
    },
  ];

  const getStatus = (survey) => {
    return (
      <span className={`surveyStatus ${survey.attributes.status}`}>
        {capitalizeFirstLetter(survey.attributes.status)}{" "}
        {(survey.attributes.status === "revised" ||
          survey.attributes.status === "applied") &&
          survey.attributes.projectId && (
            <>
              (
              <a
                onClick={(e) =>
                  window.open(
                    `/projects/${survey.attributes.projectId}/edit`,
                    "_blank"
                  )
                }
              >
                View Project
              </a>
              )
            </>
          )}
      </span>
    );
  };

  const getSurveyName = (survey) => {
    return (
      <span
        className="surveyName"
        onClick={() =>
          navigate(`/surveys/${survey.attributes.questionnaireId}/take`, {
            state: { surveyId: survey.id },
          })
        }
      >
        {capitalizeFirstLetter(survey.attributes.name)}
      </span>
    );
  };

  const tableItems: SharedSurvey[] = [];
  sortedSurveys.data.forEach((survey, i) => {
    const formatted = {
      id: survey.id,
      checked: survey.checked,
      attributes: {
        name: getSurveyName(survey),
        status: getStatus(survey),
        sentBy: survey.attributes.sentBy,
        sentTo: survey.attributes.sentTo,
        sentOn: survey.attributes.sentOn,
      },
    };
    tableItems.push(formatted);
  });

  const handleSort = (column) => {
    const split = column.split(" ");
    let name = split[split.length - 1];
    if (split[0] === "Sent") {
      name = split[0].toLowerCase() + name;
    } else {
      name = name.toLowerCase();
    }

    const sorted = sortItemsByAttribute(
      name === "sentOn" ? null : sortedSurveys,
      name,
      sortBy
    );

    if (name !== "sentOn") {
      setSortedSurveys(sorted.data);
    } else {
      if (sorted.columns.sentOn !== "") {
        setSortedSurveys({
          ...sortedSurveys,
          data: sortItemsByDate(sortedSurveys.data, name, sortBy),
        });
      }
    }
    setSortBy(sorted.columns);
  };

  return (
    <>
      <FEPaginatedTable
        tableType="surveys"
        tableItemsData={{
          ...surveysData,
          data: tableItems,
        }}
        columnHeaders={columnHeaders}
        checkboxes={false}
        filtered={filtering}
        checkedItems={[]}
        setCheckedItems={() => null}
        pageSizeEnabled
        onSort={handleSort}
      />
    </>
  );
}

export default SharedSurveys;
